import { isEmpty } from 'lodash-es';
import { useRouter } from 'next/router';
import { useCallback, useEffect, useRef } from 'react';
import { useSessionstorageState } from 'rooks';
import shallow from 'zustand/shallow';

import useVideoPlayerStore from '../../store';

interface CuePoint {
  start: number;
  end: number;
  duration: number;
  preroll: boolean;
  postroll: boolean;
  played: boolean;
}

// Define proper types for event handlers
type EventHandler = (() => void) | null;

export const usePlayerEvents = (player: HTMLVideoElement | null) => {
  const router = useRouter();

  // Only select the state values we need for the time update handler
  const {
    ad,
    contentState,
    currentTime,
    playbackMode,
    playbackRate,
    prerollDuration,
    postrollDuration,
    subtitleCues,
    onSubtitleCue,
    onCurrentTime,
    onContentTime,
    onStreamTime,
    onContentState,
    onBufferState
  } = useVideoPlayerStore(
    state => ({
      ad: state.ad,
      contentState: state.contentState,
      currentTime: state.currentTime,
      playbackMode: state.playbackMode,
      playbackRate: state.playbackRate,
      prerollDuration: state.prerollDuration,
      postrollDuration: state.postrollDuration,
      subtitleCues: state.subtitleCues,
      onSubtitleCue: state.onSubtitleCue,
      onCurrentTime: state.onCurrentTime,
      onContentTime: state.onContentTime,
      onStreamTime: state.onStreamTime,
      onContentState: state.onContentState,
      onBufferState: state.onBufferState
    }),
    shallow
  );

  // Separate store subscription for transcript-related state
  const { mixpanelTalkDetails, onTranscriptToggle } = useVideoPlayerStore(
    state => ({
      mixpanelTalkDetails: state.mixpanelTalkDetails,
      onTranscriptToggle: state.onTranscriptToggle
    }),
    shallow
  );

  const cues = ad?.adInfo?.cues;

  const [storedPlaybackRate, setStoredPlaybackRate] = useSessionstorageState(
    'playback_rate_state',
    playbackRate
  );

  const cleanupRef = useRef(false);
  const isMountedRef = useRef(false);

  // Store event handlers in refs to maintain identity between renders
  // This prevents unnecessary cleanup and re-attachment of listeners
  const eventHandlersRef = useRef<{
    timeUpdate: EventHandler;
    rateChange: EventHandler;
    bufferStart: EventHandler;
    bufferEnd: EventHandler;
  }>({
    timeUpdate: null,
    rateChange: null,
    bufferStart: null,
    bufferEnd: null
  });

  // Memoize the subtitle cue update function
  const updateSubtitleCue = useCallback(
    (currentTime: number) => {
      const currentCue = subtitleCues.find(
        (cue: { startTime: number; endTime: number }) =>
          currentTime >= cue.startTime && currentTime <= cue.endTime
      );

      if (currentCue) {
        onSubtitleCue(currentCue.text);
      } else {
        onSubtitleCue('');
      }
    },
    [subtitleCues, onSubtitleCue]
  );

  // Time update handler with all logic for managing playback position
  const handleTimeUpdate = useCallback(() => {
    if (!player || cleanupRef.current) {
      return;
    }

    const streamTime = player.currentTime;
    const duration = player.duration;

    if (!Number.isFinite(duration) || duration <= 0) {
      if (
        playbackMode !== 'unstarted' &&
        playbackMode !== 'ended' &&
        contentState === 'content'
      ) {
        console.warn(
          { duration },
          'Invalid duration in timeUpdateHandler, using last known good values'
        );
      } else {
        return;
      }
    }

    if (!Number.isFinite(streamTime)) {
      console.warn({ streamTime }, 'Invalid streamTime in timeUpdateHandler');
      return;
    }

    const postrollStart =
      Number.isFinite(duration) && duration > 0
        ? duration - (postrollDuration || 0)
        : 0;
    const contentDuration = postrollStart - (prerollDuration || 0);
    let adjustedCurrentTime = streamTime - (prerollDuration || 0);

    // Find postroll cue point if it exists
    const postrollCue = cues?.find((cue: CuePoint) => cue.postroll);

    // Determine if we're in or approaching postroll
    const actualPostrollStart = postrollCue?.start ?? postrollStart;

    // Only proceed with postroll logic if we have valid time values
    if (Number.isFinite(streamTime) && Number.isFinite(actualPostrollStart)) {
      const timeToPostroll = actualPostrollStart - streamTime;
      const isApproachingPostroll =
        timeToPostroll > 0 && timeToPostroll <= 0.05;
      const isInPostroll = streamTime >= actualPostrollStart;

      // Only transition if we're just about to hit the exact cue point
      if (
        isApproachingPostroll &&
        !isInPostroll &&
        postrollDuration &&
        postrollCue &&
        contentState !== 'postroll'
      ) {
        adjustedCurrentTime = contentDuration;
        player.currentTime = actualPostrollStart;
        onContentState('postroll');
        return;
      }

      // Safety check - if we somehow jumped past postroll start, bring it back
      if (
        timeToPostroll < 0 &&
        timeToPostroll > -0.5 &&
        !isInPostroll &&
        postrollDuration &&
        postrollCue &&
        contentState !== 'postroll'
      ) {
        player.currentTime = actualPostrollStart;
        onContentState('postroll');
        return;
      }
    }

    // Ensure we stay within valid bounds
    const validContentTime = Math.max(
      0,
      Math.min(adjustedCurrentTime, contentDuration || 0)
    );

    // Update all time tracking states
    onStreamTime(streamTime);
    onCurrentTime(validContentTime);
    onContentTime(Math.ceil(validContentTime) * 1000);

    // Update subtitles if available
    if (subtitleCues.length > 0) {
      updateSubtitleCue(validContentTime);
    }
  }, [
    player,
    postrollDuration,
    prerollDuration,
    cues,
    contentState,
    subtitleCues,
    onStreamTime,
    onCurrentTime,
    onContentTime,
    onContentState,
    updateSubtitleCue,
    playbackMode
  ]);

  // Track mount state
  useEffect(() => {
    isMountedRef.current = true;
    return () => {
      isMountedRef.current = false;
      cleanupRef.current = true;
    };
  }, []);

  // Create persistent event handlers that maintain identity between renders
  useEffect(() => {
    if (!player) return;

    // Define handlers with stable identities
    eventHandlersRef.current.rateChange = () => {
      if (isMountedRef.current) {
        setStoredPlaybackRate(player.playbackRate);
      }
    };

    eventHandlersRef.current.bufferStart = () => {
      if (!player.paused && !player.seeking) {
        onBufferState(true);
      }
    };

    eventHandlersRef.current.bufferEnd = () => onBufferState(false);

    // timeUpdate handler is already defined via useCallback

    // Cleanup function will be called when component unmounts or player changes
    return () => {
      // Clear all handlers to avoid closure issues
      eventHandlersRef.current = {
        timeUpdate: null,
        rateChange: null,
        bufferStart: null,
        bufferEnd: null
      };
    };
  }, [player, setStoredPlaybackRate, onBufferState]);

  // Handle playback rate changes
  useEffect(() => {
    if (!player) return;

    const handleRateChange = eventHandlersRef.current.rateChange;
    if (!handleRateChange) return;

    player.addEventListener('ratechange', handleRateChange);
    return () => player.removeEventListener('ratechange', handleRateChange);
  }, [player]);

  // Sync playback rate
  useEffect(() => {
    if (
      player &&
      (player.playbackRate !== storedPlaybackRate ||
        player.playbackRate !== playbackRate) &&
      playbackMode !== 'unstarted' &&
      playbackMode !== 'ended' &&
      contentState === 'content' &&
      currentTime > 0
    ) {
      player.playbackRate = storedPlaybackRate || playbackRate;
    }
  }, [
    player,
    playbackRate,
    playbackMode,
    storedPlaybackRate,
    contentState,
    currentTime
  ]);

  // Handle time updates
  useEffect(() => {
    if (!player || playbackMode === 'ended' || playbackMode === 'unstarted') {
      return;
    }

    player.addEventListener('timeupdate', handleTimeUpdate);
    return () => {
      player.removeEventListener('timeupdate', handleTimeUpdate);
    };
  }, [player, playbackMode, handleTimeUpdate]);

  // Set up buffering event listeners
  useEffect(() => {
    if (!player) return;

    const handleBufferStart = eventHandlersRef.current.bufferStart;
    const handleBufferEnd = eventHandlersRef.current.bufferEnd;

    if (!handleBufferStart || !handleBufferEnd) return;

    player.addEventListener('waiting', handleBufferStart);
    player.addEventListener('playing', handleBufferEnd);
    player.addEventListener('canplay', handleBufferEnd);

    return () => {
      player.removeEventListener('waiting', handleBufferStart);
      player.removeEventListener('playing', handleBufferEnd);
      player.removeEventListener('canplay', handleBufferEnd);
    };
  }, [player]);

  // Handle transcript visibility based on URL
  useEffect(() => {
    const slug = router.query.slug;
    if (
      Array.isArray(slug)
        ? slug.some(s => s.includes('transcript'))
        : slug?.includes('transcript') && !isEmpty(mixpanelTalkDetails)
    ) {
      onTranscriptToggle(true);
    }
  }, [router.query.slug, mixpanelTalkDetails, onTranscriptToggle]);
};
